import { produce, Draft } from 'immer'
import { IAction, TActionTypes } from '@Root/types'
import map from 'lodash/map'
import findIndex from 'lodash/findIndex'
import filter from 'lodash/filter'
import without from 'lodash/without'

import {
  IWorkspaceState,
  WorkspaceActionTypes,
  TWorkspaceActionTypes,
  ISetCurrentWorkspacePayload,
  ICreateWorkspaceSuccessResponseBody,
  IRenameWorkspaceSuccessPayload,
  IDeleteWorkspacesSuccessPayload,
  IChangeSelectedWorkspacesPayload,
} from './types'

import {
  TAccountActionTypes,
  AccountActionTypes,
  IFetchWorkspacesSuccessResponseBody,
} from '@SiteModules/Account/types'

import {
  TElementActionTypes,
  ElementActionTypes,
  ISubmitUrlsSuccessResponseBody,
} from '@SiteModules/Element/types'

import {
  TProjectActionTypes,
  ProjectActionTypes,
  IDeleteProjectsSuccessPayload,
} from '@SiteModules/Project/types'

import {
  ProjectVersionSettingsTtsActionTypes,
} from '@SiteModules/ProjectVersionSettingsTts/types'

export const initialState: IWorkspaceState = {
  workspaces: [],
  currentWorkspaceId: -1,
  selectedWorkspaceIds: [],
  saving: false,
  hasRenamedWorkspaceSuccessfully: false
}

type TAction = TActionTypes | TAccountActionTypes | TWorkspaceActionTypes | TElementActionTypes | TProjectActionTypes

export const workspacesReducer = (
  state: IWorkspaceState = initialState,
  action: TAction
): IWorkspaceState => {
  return produce(state, draft => {
    switch (action.type) {
      case WorkspaceActionTypes.RESET_CURRENT_WORKSPACE:
        resetCurrentWorkspace(draft)
        break

      case WorkspaceActionTypes.RESET_WORKSPACES:
        resetWorkspaces(draft)
        break

      case AccountActionTypes.FETCH_WORKSPACES_SUCCESS:
        fetchWorkspacesSuccess(draft, action)
        break

      case WorkspaceActionTypes.SET_CURRENT_WORKSPACE:
        setCurrentWorkspace(draft, action)
        break

      case WorkspaceActionTypes.CREATE_WORKSPACE_SUCCESS:
        createWorkspaceSuccess(draft, action)
        break

      case WorkspaceActionTypes.RENAME_WORKSPACE:
        renameWorkspace(draft)
        break

      case WorkspaceActionTypes.RENAME_WORKSPACE_SUCCESS:
        renameWorkspaceSuccess(draft, action)
        break

      case WorkspaceActionTypes.RENAME_WORKSPACE_FAILURE:
        renameWorkspaceFailure(draft)
        break

      case WorkspaceActionTypes.DELETE_WORKSPACES_SUCCESS:
        deleteWorkspacesSuccess(draft, action)
        break

      case WorkspaceActionTypes.CHANGE_SELECTED_WORKSPACES:
        changeSelectedWorkspaces(draft, action)
        break

      case WorkspaceActionTypes.UNSELECT_WORKSPACES:
        unselectWorkspaces(draft)
        break

      case WorkspaceActionTypes.SELECT_ALL_WORKSPACES:
        selectAllWorkspaces(draft)
        break

      case ElementActionTypes.CREATE_ELEMENT_SUCCESS:
      case ProjectActionTypes.CREATE_PROJECT_SUCCESS:
      case ProjectVersionSettingsTtsActionTypes.SETUP_TTS_SUCCESS:
        updateNumberOfProjectsByOne(draft)
        break

      case ElementActionTypes.SUBMIT_URLS_SUCCESS:
        updateNumberOfProjectsAfterSubmitUrls(draft, action)
        break

      case ProjectActionTypes.DELETE_PROJECTS_SUCCESS:
        updateNumberOfProjectsAfterDeleteProjects(draft, action)
        break

      default:
        break
    }
  })
}

const resetCurrentWorkspace = (draft: Draft<IWorkspaceState>) => {
  draft.currentWorkspaceId = draft.workspaces.length > 0 ? draft.workspaces[0].id : initialState.currentWorkspaceId
}

const resetWorkspaces = (draft: Draft<IWorkspaceState>) => {
  draft.workspaces = []
}

function isFetchWorkspacesSuccessAction(arg: any): arg is IAction<IFetchWorkspacesSuccessResponseBody> {
  return arg.type === AccountActionTypes.FETCH_WORKSPACES_SUCCESS
}

const fetchWorkspacesSuccess = (draft: Draft<IWorkspaceState>, action: TAction) => {
  if (!isFetchWorkspacesSuccessAction(action)) { return }

  draft.workspaces = map(action.payload.data, data => {
    return ({
      id: parseInt(data.id, 10),
      attributes: data.attributes
    })
  })
}

function isSetCurrentWorkspaceAction(arg: any): arg is IAction<ISetCurrentWorkspacePayload> {
  return arg.type === WorkspaceActionTypes.SET_CURRENT_WORKSPACE
}

const setCurrentWorkspace = (draft: Draft<IWorkspaceState>, action: TAction) => {
  if (!isSetCurrentWorkspaceAction(action)) { return }

  draft.currentWorkspaceId = action.payload.workspaceId
}

function isCreateWorkspaceSuccessAction(arg: any): arg is IAction<ICreateWorkspaceSuccessResponseBody> {
  return arg.type === WorkspaceActionTypes.CREATE_WORKSPACE_SUCCESS
}

const createWorkspaceSuccess = (draft: Draft<IWorkspaceState>, action: TAction) => {
  if (!isCreateWorkspaceSuccessAction(action)) { return }

  const addedWorkspace = { id: parseInt(action.payload.data.id, 10), attributes: action.payload.data.attributes }
  draft.workspaces = [addedWorkspace].concat(draft.workspaces)
}

const renameWorkspace = (draft: Draft<IWorkspaceState>) => {
  draft.saving = true
  draft.hasRenamedWorkspaceSuccessfully = false
}

function isRenameWorkspaceSuccessAction(arg: any): arg is IAction<IRenameWorkspaceSuccessPayload> {
  return arg.type === WorkspaceActionTypes.RENAME_WORKSPACE_SUCCESS
}

const renameWorkspaceSuccess = (draft: Draft<IWorkspaceState>, action: TAction) => {
  if (!isRenameWorkspaceSuccessAction(action)) { return }

  const workspaces = draft.workspaces
  const workspaceToRenameIndex = findIndex(workspaces, workspace => workspace.id === action.payload.workspaceId)
  draft.workspaces[workspaceToRenameIndex].attributes.name = action.payload.name
  draft.saving = false
  draft.hasRenamedWorkspaceSuccessfully = true
}

const renameWorkspaceFailure = (draft: Draft<IWorkspaceState>) => {
  draft.saving = false
  draft.hasRenamedWorkspaceSuccessfully = false
}

function isDeleteWorkspacesSuccessAction(arg: any): arg is IAction<IDeleteWorkspacesSuccessPayload> {
  return arg.type === WorkspaceActionTypes.DELETE_WORKSPACES_SUCCESS
}

const deleteWorkspacesSuccess = (draft: Draft<IWorkspaceState>, action: TAction) => {
  if (!isDeleteWorkspacesSuccessAction(action)) { return }

  draft.workspaces = filter(draft.workspaces, workspace => !action.payload.workspaceIds.includes(workspace.id))
  draft.selectedWorkspaceIds = []
}

function isIChangeSelectedWorkspacesAction(arg: any): arg is IAction<IChangeSelectedWorkspacesPayload> {
  return arg.type === WorkspaceActionTypes.CHANGE_SELECTED_WORKSPACES
}

const changeSelectedWorkspaces = (draft: Draft<IWorkspaceState>, action: TAction) => {
  if (!isIChangeSelectedWorkspacesAction(action)) { return }

  const { selectWorkspace, workspaceId } = action.payload

  if (selectWorkspace) {
    draft.selectedWorkspaceIds.push(workspaceId)
  } else {
    draft.selectedWorkspaceIds = without(draft.selectedWorkspaceIds, workspaceId)
  }
}

const unselectWorkspaces = (draft: Draft<IWorkspaceState>) => {
  draft.selectedWorkspaceIds = []
}

const selectAllWorkspaces = (draft: Draft<IWorkspaceState>) => {
  draft.selectedWorkspaceIds = map(draft.workspaces, workspace => workspace.id)
}

const updateNumberOfProjectsByOne = (draft: Draft<IWorkspaceState>) => {
  const workspaces = draft.workspaces
  const currentWorkspaceIndex = findIndex(workspaces, workspace => workspace.id === draft.currentWorkspaceId)

  draft.workspaces[currentWorkspaceIndex].attributes.numberOfProjects += 1
}

function isISubmitUrlsSuccessAction(arg: any): arg is IAction<ISubmitUrlsSuccessResponseBody> {
  return arg.type === ElementActionTypes.SUBMIT_URLS_SUCCESS
}

const updateNumberOfProjectsAfterSubmitUrls = (draft: Draft<IWorkspaceState>, action: TAction) => {
  if (!isISubmitUrlsSuccessAction(action)) { return }

  const workspaces = draft.workspaces
  const currentWorkspaceIndex = findIndex(workspaces, workspace => workspace.id === draft.currentWorkspaceId)

  draft.workspaces[currentWorkspaceIndex].attributes.numberOfProjects += action.payload.projectVersions.data.length
}

function isIDeleteProjectsSuccessAction(arg: any): arg is IAction<IDeleteProjectsSuccessPayload> {
  return arg.type === ProjectActionTypes.DELETE_PROJECTS_SUCCESS
}

const updateNumberOfProjectsAfterDeleteProjects = (draft: Draft<IWorkspaceState>, action: TAction) => {
  if (!isIDeleteProjectsSuccessAction(action)) { return }

  const workspaces = draft.workspaces
  const currentWorkspaceIndex = findIndex(workspaces, workspace => workspace.id === draft.currentWorkspaceId)

  draft.workspaces[currentWorkspaceIndex].attributes.numberOfProjects -= action.payload.projectIds.length
}
